/**
 * Per-component AI-prompt metadata — descriptions and concrete examples.
 *
 * The Zod schemas in `catalog.ts` give the AI a type contract; this
 * module gives it the *intent* (what the component is for) and *one
 * realistic example* of valid props. Both get emitted verbatim into
 * the system prompt by `prompt-generator.ts`.
 *
 * Why: AI quality on first-shot generation is dramatically better
 * when the prompt shows a working example of a non-trivial element
 * (Tabs, Grid, Accordion, Table) rather than asking the model to
 * synthesise the shape from prop types alone.
 *
 * Examples were drawn from real successful spec generations; keep
 * them small (one element each) so the prompt stays compact.
 */

export interface ComponentMetadata {
  /** One-line summary of what the component is for. */
  description: string
  /**
   * A concrete, realistic value for this component, including the
   * `type` discriminator so the example is a complete spec snippet.
   * Children/data fields use minimal but valid placeholders.
   */
  example: Record<string, unknown>
}

/**
 * Keyed by the literal `type` field on each component schema.
 * Add metadata here whenever a new component lands in the catalog.
 */
export const componentMetadata: Record<string, ComponentMetadata> = {
  // ── Charts ───────────────────────────────────────────────────────
  BarChart: {
    description:
      "Categorical bar chart. Use for category-vs-measure comparisons (revenue per region, count per status). For stacked bars, give each bar the same `stackId`.",
    example: {
      type: "BarChart",
      title: "Revenue by region",
      data: { $state: "/data/revenue_by_region" },
      xKey: "region",
      bars: [{ dataKey: "revenue", color: "hsl(var(--chart-1))" }],
    },
  },
  LineChart: {
    description:
      "Time-series or ordered-x line chart. Use for trends over time or any continuous-x measure. Each `lines[]` entry plots a separate series.",
    example: {
      type: "LineChart",
      title: "Daily active users",
      data: { $state: "/data/dau" },
      xKey: "date",
      lines: [{ dataKey: "users", color: "hsl(var(--success-ink))" }],
    },
  },
  AreaChart: {
    description:
      "Filled-area version of LineChart. Use when the magnitude (not just the shape) matters — total volume over time, cumulative metrics.",
    example: {
      type: "AreaChart",
      title: "Cumulative sessions",
      data: { $state: "/data/sessions" },
      xKey: "month",
      areas: [{ dataKey: "sessions", color: "hsl(var(--chart-3))" }],
    },
  },
  PieChart: {
    description:
      "Categorical share-of-whole. Prefer BarChart when there are more than 6–7 slices or when comparing magnitudes precisely.",
    example: {
      type: "PieChart",
      title: "Traffic by source",
      data: { $state: "/data/sources" },
      nameKey: "source",
      valueKey: "visits",
    },
  },

  // ── Data display ─────────────────────────────────────────────────
  KPICard: {
    description:
      "Single headline metric with optional comparison and trend arrow. Use for the dashboard's most important numbers — typically 2–4 per dashboard.",
    example: {
      type: "KPICard",
      label: "MRR",
      value: { $format: "currency", value: { $state: "/kpi/mrr" }, currency: "SEK" },
      comparison: { value: { $state: "/kpi/mrr_delta" }, format: "percent" },
    },
  },
  Table: {
    description:
      "Tabular data view with optional column formatting. Use for row-level detail; for aggregates prefer KPICard or a chart.",
    example: {
      type: "Table",
      title: "Top customers",
      data: { $state: "/data/customers" },
      columns: [
        { key: "name", label: "Customer" },
        { key: "revenue", label: "Revenue", format: "currency" },
      ],
    },
  },
  Text: {
    description:
      "Static or expression-driven text block. Supports $template / $format / $t for dynamic content. Use for narrative captions, headers, and inline computed values.",
    example: {
      type: "Text",
      content: { $template: "Showing data for ${/filters/region}" },
      variant: "muted",
    },
  },
  Separator: {
    description: "Horizontal divider between sections. No props beyond `type`.",
    example: { type: "Separator" },
  },
  Alert: {
    description:
      "Banner for warnings, info, or errors. Variant controls colour. Title required; children/description optional.",
    example: {
      type: "Alert",
      variant: "warning",
      title: "Data is older than 24h",
      description: "Last refresh: yesterday at 14:00",
    },
  },
  ProgressBar: {
    description:
      "Linear progress indicator. Use for completion rates, quotas, milestone progress. Value is 0–1 by default; pass `max` to override.",
    example: {
      type: "ProgressBar",
      label: "Onboarded users",
      value: { $state: "/kpi/onboarded_pct" },
    },
  },

  // ── Layout ───────────────────────────────────────────────────────
  Grid: {
    description:
      "Responsive CSS grid container. `columns` is a number (1–6) or a per-breakpoint object like `{ sm: 1, md: 2, lg: 3 }`. Children flow left-to-right.",
    example: {
      type: "Grid",
      columns: { sm: 1, lg: 3 },
      children: [
        { type: "KPICard", label: "Users", value: 1234 },
        { type: "KPICard", label: "Revenue", value: 56_789 },
        { type: "KPICard", label: "Conversion", value: 0.042 },
      ],
    },
  },
  Row: {
    description:
      "Horizontal flex row. Use when you need precise alignment of a small number of widgets that shouldn't reflow into multiple lines.",
    example: {
      type: "Row",
      gap: "md",
      children: [
        { type: "Text", content: "Showing region:" },
        { type: "Select", stateKey: "/filters/region", options: [{ label: "EU", value: "eu" }] },
      ],
    },
  },

  // ── Interactive layout ───────────────────────────────────────────
  Tabs: {
    description:
      "Tabbed container. Each tab has a `label`, a `value` used for state/keying, and its own `children` array.",
    example: {
      type: "Tabs",
      defaultValue: "overview",
      tabs: [
        {
          label: "Overview",
          value: "overview",
          children: [{ type: "KPICard", label: "Users", value: 1234 }],
        },
        {
          label: "Detail",
          value: "detail",
          children: [{ type: "Table", data: { $state: "/data/users" }, columns: [] }],
        },
      ],
    },
  },
  Accordion: {
    description:
      "Collapsible sections. `type:'single'` allows one open at a time; `'multiple'` allows several. Each item has a `title` and `children`.",
    example: {
      type: "Accordion",
      accordionType: "single",
      items: [
        {
          title: "Sales",
          value: "sales",
          children: [{ type: "BarChart", data: [], xKey: "x", bars: [] }],
        },
      ],
    },
  },

  // ── Interactive inputs ───────────────────────────────────────────
  Select: {
    description:
      "Dropdown bound to a state path via `stateKey`. Changing the value writes to state without an AI roundtrip — queryBindings + watchBindings react automatically.",
    example: {
      type: "Select",
      stateKey: "/filters/region",
      placeholder: "All regions",
      options: [
        { label: "Europe", value: "eu" },
        { label: "Americas", value: "am" },
      ],
    },
  },
  Checkbox: {
    description:
      "Boolean toggle bound to a state path via `stateKey`. Same non-AI write semantics as Select.",
    example: {
      type: "Checkbox",
      stateKey: "/filters/include_trial_users",
      label: "Include trial users",
    },
  },
  TextInput: {
    description:
      "Free-text input bound to `stateKey`. Use for search/filter strings; pair with `placeholder`. State writes are immediate (de-bounce in the consuming binding if needed).",
    example: {
      type: "TextInput",
      stateKey: "/filters/search",
      placeholder: "Search customers…",
    },
  },

  // ── shadcn primitives (§3.2) ─────────────────────────────────────
  Heading: {
    description:
      "Semantic heading. `level` is 1–6 (defaults to 2). Use for section breaks within Tabs/Accordion content; for the dashboard's top-level title use the `title` field on the root spec.",
    example: { type: "Heading", text: "Customers this quarter", level: 3 },
  },
  Badge: {
    description:
      'Status pill. Variants: "default" | "secondary" | "destructive" | "outline". Use for tags, status, count chips. Pair with $template/$state for live values.',
    example: { type: "Badge", text: "Beta", variant: "secondary" },
  },
  Avatar: {
    description:
      "Round avatar with image + initials fallback. `name` is required (used to derive initials). `src` optional. Sizes: sm | md (default) | lg.",
    example: {
      type: "Avatar",
      name: { $item: "name" },
      src: { $item: "avatar_url" },
      size: "sm",
    },
  },
  Tooltip: {
    description:
      "Hover-text wrapper for a single child element. Use for short explanations on KPI cards / chart titles / icons. The wrapped element is in `child`, the popover text in `content`.",
    example: {
      type: "Tooltip",
      content: "Year-over-year growth",
      child: { type: "Badge", text: "+12%", variant: "default" },
    },
  },

  // ── Data-driven layout ───────────────────────────────────────────
  Repeat: {
    description:
      'Render `item` once per element of `source`. Inside `item`, `{$item: "field"}` reads a property of the current row and `{$index: true}` returns the row index. Use to fan out a row template instead of having the AI emit N concrete elements.',
    example: {
      type: "Repeat",
      source: { $state: "/data/customers" },
      keyField: "id",
      item: {
        type: "Card",
        title: { $item: "name" },
        children: [{ type: "Text", content: { $item: "email" } }],
      },
    },
  },
}

/**
 * Look up metadata for a component by type name. Returns `undefined`
 * if no metadata is registered — caller should fall back to the
 * schema-derived prop list alone.
 */
export function getComponentMetadata(typeName: string): ComponentMetadata | undefined {
  return componentMetadata[typeName]
}
